import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Popover,
  TextField,
  MenuItem,
  Checkbox,
  Button,
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import useTranslation from "Hooks/useTranslation";
import engineIcon from "assets/img/car-engine.svg";
import AssetDetailModal from "./AssetDetailModal";
import CircularProgress from "@material-ui/core/CircularProgress";
import MmsApiService from "Services/MMS/mms-api.service";
import TuneIcon from "@material-ui/icons/Tune";
import { Autocomplete } from "@material-ui/lab";
import debounce from "lodash.debounce";

const SegmentedProgressBar = ({
  value = 0,
  segments = 30,
  barHeight = 22,
  barRadius = 16,
  borderColor = "#00eaff",
  backgroundColor = "#071a2d",
  segmentWidth = 10.5,
  segmentGap = 1,
}) => {
  const gradientStart = [10, 35, 64];
  const gradientEnd = [0, 234, 255];

  function getSegmentColor(idx) {
    const t = idx / (segments - 1);
    const r = Math.round(
      gradientStart[0] + (gradientEnd[0] - gradientStart[0]) * t
    );
    const g = Math.round(
      gradientStart[1] + (gradientEnd[1] - gradientStart[1]) * t
    );
    const b = Math.round(
      gradientStart[2] + (gradientEnd[2] - gradientStart[2]) * t
    );
    return `rgb(${r},${g},${b})`;
  }

  const activeSegments = Math.round((value / 100) * segments);

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        height: barHeight,
        background: backgroundColor,
        borderRadius: barRadius,
        border: `1px solid ${borderColor}`,
        padding: 2,
        boxSizing: "border-box",
        justifyContent: "flex-start",
      }}
    >
      {Array.from({ length: segments }).map((_, i) => (
        <div
          key={i}
          style={{
            width: segmentWidth,
            height: "80%",
            borderRadius: 2,
            marginLeft: i === 0 ? 0 : segmentGap,
            background: i < activeSegments ? getSegmentColor(i) : "#0a2340",
            transition: "background 0.3s ease",
            animation:
              i < activeSegments
                ? `segmentAppear 0.6s ease ${i * 0.05}s both`
                : "none",
            opacity: i < activeSegments ? 1 : 0.3,
          }}
        />
      ))}
      <style>{`
        @keyframes segmentAppear {
          from {
            opacity: 0;
            transform: scaleY(0.3);
          }
          to {
            opacity: 1;
            transform: scaleY(1);
          }
        }
        
        .marker-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: all 0.2s ease;
        }
        
        .marker-icon:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        }
        
       
        
        .MuiTabs-root {
          min-height: 48px;
        }
        
        .MuiTab-root {
          min-height: 48px;
          font-size: 12px;
          font-weight: 600;
        }
        
        .MuiTabs-indicator {
          height: 3px;
        }
        
        .asset-item {
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.2s ease;
        }
        
        .asset-item:hover {
          background-color: rgba(0,0,0,0.05);
        }
        
        .asset-item:last-child {
          border-bottom: none;
        }
        
        .map-loading-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 0.9);
          padding: 20px;
          border-radius: 8px;
          z-index: 1000;
        }
      `}</style>
    </div>
  );
};

const Map = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const translation = useTranslation();
  const scrollContainerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedAssetInList, setSelectedAssetInList] = useState(null);
  const [isAssetClicked, setIsAssetClicked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showAssetsPanel, setShowAssetsPanel] = useState(false);
  const [mapData, setMapData] = useState([]);
  const [allAssetsData, setAllAssetsData] = useState([]);

  const [searchAssetsData, setSearchAssetsData] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [tempSelectedAssets, setTempSelectedAssets] = useState([]);

  const [searchAlertsData, setSearchAlertsData] = useState([]);
  const [searchAlertsInputValue, setSearchAlertsInputValue] = useState("");
  const [tempSelectedUnits, setTempSelectedUnits] = useState([]);
  const [tempSelectedMetrics, setTempSelectedMetrics] = useState([]);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [tempSortBy, setTempSortBy] = useState("Date");
  const [tempSortDirection, setTempSortDirection] = useState("desc");
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [allAlertsComponents, setAllAlertsComponents] = useState([]);
  // Видалено hasLoadedWithFilters та hasLoadedAlertsWithFilters - більше не потрібні
  const [alertsData, setAlertsData] = useState([]);
  
  // Окремі стани для фільтрів (не впливають на основні списки)
  const [filterAssetsPage, setFilterAssetsPage] = useState(1);
  const [filterAlertsPage, setFilterAlertsPage] = useState(1);
  const [hasMoreFilterAssets, setHasMoreFilterAssets] = useState(true);
  const [hasMoreFilterAlerts, setHasMoreFilterAlerts] = useState(true);
  const [isLoadingMoreFilterAssets, setIsLoadingMoreFilterAssets] = useState(false);
  const [isLoadingMoreFilterAlerts, setIsLoadingMoreFilterAlerts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterTypeMetrics, setFilterTypeMetrics] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  const [assetsFilters, setAssetsFilters] = useState({
    selectedAssets: [],
    sortBy: "LastHealthCheckDate",
    sortDirection: "desc",
    pageSize: 15,
    pageNumber: 1,
    searchTerm: "",
  });

  const [alertsFilters, setAlertsFilters] = useState({
    selectedUnits: [],
    selectedMetrics: [],
    startDate: null,
    endDate: null,
    searchTerm: "",
    sortBy: "Date",
    sortDirection: "desc",
    pageSize: 15,
    pageNumber: 1,
  });

  const [isLoadingMoreAssets, setIsLoadingMoreAssets] = useState(false);
  const [isLoadingMoreAlerts, setIsLoadingMoreAlerts] = useState(false);
  const [hasMoreAssets, setHasMoreAssets] = useState(true);
  const [hasMoreAlerts, setHasMoreAlerts] = useState(true);

  const [totalAssetsCount, setTotalAssetsCount] = useState(0);
  const [totalAlertsCount, setTotalAlertsCount] = useState(0);
  
  // Дедуплікація запитів
  const [isAssetsRequestInProgress, setIsAssetsRequestInProgress] = useState(false);
  const [isAlertsRequestInProgress, setIsAlertsRequestInProgress] = useState(false);
  const hasInitialDataLoadedRef = useRef(false);

  const toggleFilterTypeMetrics = () => {
    setFilterTypeMetrics(!filterTypeMetrics);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);

    if (activeTab === 0) {
      setSearchAssetsData([]);
      setSearchInputValue("");

      setTempSelectedAssets(assetsFilters.selectedAssets);
    } else if (activeTab === 1) {
      setSearchAlertsData([]);
      setSearchAlertsInputValue("");

      const selectedUnitsAsObjects = alertsFilters.selectedUnits.map(
        (unitUId) => {
          const component = allAlertsComponents.find(
            (comp) => comp.unitUId === unitUId
          );
          return component
            ? {
                componentName: component.componentName,
                componentNumber: component.componentNumber,
                unitUId: component.unitUId,
              }
            : { componentName: unitUId, componentNumber: "", unitUId: unitUId };
        }
      );
      setTempSelectedUnits(selectedUnitsAsObjects);
    }
  };

  useEffect(() => {
    const loadData = () => {
      const savedAssetsFilters = localStorage.getItem("mapAssetsFilters");
      const savedAlertsFilters = localStorage.getItem("mapAlertsFilters");
      const compId = localStorage.getItem("_company_id_");

      if (savedAssetsFilters) {
        const parsedFilters = JSON.parse(savedAssetsFilters);
        setAssetsFilters(parsedFilters);
      }
      if (savedAlertsFilters) {
        setAlertsFilters(JSON.parse(savedAlertsFilters));
      }
      
      if (compId) {
        setCompanyId(compId);
      } else {
        setTimeout(() => {
          const retryCompId = localStorage.getItem("_company_id_");
          if (retryCompId) {
            setCompanyId(retryCompId);
          }
        }, 100);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    localStorage.setItem("mapAssetsFilters", JSON.stringify(assetsFilters));
  }, [assetsFilters]);

  useEffect(() => {
    localStorage.setItem("mapAlertsFilters", JSON.stringify(alertsFilters));
  }, [alertsFilters]);

  useEffect(() => {
    setTempSelectedAssets(assetsFilters.selectedAssets);
  }, [assetsFilters.selectedAssets]);

  const handleAssetsFilterChange = (filterType, value) => {
    setAssetsFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const searchAssets = useCallback(
    async (searchTerm) => {
      try {
        if (!searchTerm.trim()) {
          setSearchAssetsData([]);
          return;
        }

        // Використовуємо кешовані дані якщо можливо
        if (allAssetsData.length > 0) {
          const filteredAssets = allAssetsData.filter(asset => 
            asset.componentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            asset.componentNumber.toLowerCase().includes(searchTerm.toLowerCase())
          );
          setSearchAssetsData(filteredAssets);
          return;
        }

        const requestBody = {
          componentIds: [],
          filterComponentIds: [],
          sortBy: "LastHealthCheckDate",
          isAscending: false,
          pageSize: 50,
          pageNumber: 1,
          searchTerm: searchTerm,
        };

        const response = await MmsApiService.getComponentHealthCheckPaged(
          requestBody,
          filterTypeMetrics,
          companyId
        );

        if (response && response.data) {
          const items = response.data.items || [];
          setSearchAssetsData(items);
        }
      } catch (error) {
        setSearchAssetsData([]);
      }
    },
    [companyId, filterTypeMetrics, allAssetsData]
  );

  const searchAlerts = useCallback(
    async (searchTerm) => {
      try {
        if (!searchTerm.trim()) {
          setSearchAlertsData([]);
          return;
        }

        if (allAlertsComponents.length > 0) {
          const filteredComponents = allAlertsComponents.filter(component => 
            component.componentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            component.componentNumber.toLowerCase().includes(searchTerm.toLowerCase())
          );
          setSearchAlertsData(filteredComponents);
          return;
        }

        const requestBody = {
          unitUIds: [],
          metrics: [],
          startDate: null,
          endDate: null,
          searchTerm: searchTerm,
          sortBy: "Date",
          isAscending: false,
          pageSize: 50,
          pageNumber: 1,
        };

        const response = await MmsApiService.getMetricsErrorsPaged(
          requestBody,
          filterTypeMetrics,
          companyId
        );

        if (response && response.data) {
          const items = response.data.items || [];

          const uniqueComponents = items.reduce((acc, alert) => {
            const componentName = alert.componentName;
            const componentNumber = alert.componentNumber;
            const unitUId = alert.unitUId;
            if (
              componentName &&
              unitUId &&
              !acc.find((item) => item.unitUId === unitUId)
            ) {
              acc.push({
                componentName: componentName,
                componentNumber: componentNumber,
                unitUId: unitUId,
              });
            }
            return acc;
          }, []);
          setSearchAlertsData(uniqueComponents);
        }
      } catch (error) {
        setSearchAlertsData([]);
      }
    },
    [companyId, filterTypeMetrics, allAlertsComponents]
  );

  const loadAvailableMetrics = useCallback(async () => {
    try {
      if (!companyId) return;

      const requestBody = {
        unitUIds: [],
        metrics: [],
        startDate: null,
        endDate: null,
        searchTerm: "",
        sortBy: "Date",
        isAscending: false,
        pageSize: 100,
        pageNumber: 1,
      };

      const response = await MmsApiService.getMetricsErrorsPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data && response.data.items) {
        const metrics = new Set();
        response.data.items.forEach((item) => {
          if (item.metric) {
            metrics.add(item.metric);
          }
        });
        setAvailableMetrics(Array.from(metrics).sort());
      }
    } catch (error) {
    }
  }, [companyId, filterTypeMetrics]);


  const loadMoreFilterAssets = useCallback(async () => {
    if (isLoadingMoreFilterAssets || !hasMoreFilterAssets || !companyId) return;

    setIsLoadingMoreFilterAssets(true);
    try {
      const nextPage = filterAssetsPage + 1;
      const requestBody = {
        componentIds: [],
        filterComponentIds: [],
        sortBy: "LastHealthCheckDate",
        isAscending: false,
        pageSize: 15,
        pageNumber: nextPage,
      };

      const response = await MmsApiService.getComponentHealthCheckPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data) {
        const newItems = response.data.items || [];
        setAllAssetsData((prevData) => [...prevData, ...newItems]);
        setFilterAssetsPage(nextPage);
        setHasMoreFilterAssets(newItems.length === 15);
      }
    } catch (error) {
    } finally {
      setIsLoadingMoreFilterAssets(false);
    }
  }, [isLoadingMoreFilterAssets, hasMoreFilterAssets, filterAssetsPage, filterTypeMetrics, companyId]);

  const loadMoreFilterAlerts = useCallback(async () => {
    if (isLoadingMoreFilterAlerts || !hasMoreFilterAlerts || !companyId) return;

    setIsLoadingMoreFilterAlerts(true);
    try {
      const nextPage = filterAlertsPage + 1;
      const requestBody = {
        unitUIds: [],
        metrics: [],
        startDate: null,
        endDate: null,
        searchTerm: "",
        sortBy: "Date",
        isAscending: false,
        pageSize: 15,
        pageNumber: nextPage,
      };

      const response = await MmsApiService.getMetricsErrorsPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data && response.data.items) {
        const newItems = response.data.items || [];
        const uniqueComponents = newItems.reduce((acc, alert) => {
          const componentName = alert.componentName;
          const componentNumber = alert.componentNumber;
          const unitUId = alert.unitUId;
          if (
            componentName &&
            unitUId &&
            !acc.find((item) => item.unitUId === unitUId)
          ) {
            acc.push({
              componentName: componentName,
              componentNumber: componentNumber,
              unitUId: unitUId,
            });
          }
          return acc;
        }, []);

        setAllAlertsComponents((prevData) => [...prevData, ...uniqueComponents]);
        setFilterAlertsPage(nextPage);
        setHasMoreFilterAlerts(newItems.length === 15);
      }
    } catch (error) {
    } finally {
      setIsLoadingMoreFilterAlerts(false);
    }
  }, [isLoadingMoreFilterAlerts, hasMoreFilterAlerts, filterAlertsPage, filterTypeMetrics, companyId]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm) => {
        searchAssets(searchTerm);
      }, 500),
    [searchAssets]
  );

  const debouncedAlertsSearch = useMemo(
    () =>
      debounce((searchTerm) => {
        searchAlerts(searchTerm);
      }, 500),
    [searchAlerts]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      debouncedAlertsSearch.cancel();
    };
  }, [debouncedSearch, debouncedAlertsSearch]);

  const resetFilters = () => {
    if (activeTab === 0) {
      setAssetsFilters({
        selectedAssets: [],
        sortBy: "LastHealthCheckDate",
        sortDirection: "desc",
        pageSize: 15,
        pageNumber: 1,
        searchTerm: "",
      });
      setTempSelectedAssets([]);
      setSearchInputValue("");
      setSearchAssetsData([]);
    } else {
      setAlertsFilters({
        selectedUnits: [],
        selectedMetrics: [],
        startDate: null,
        endDate: null,
        searchTerm: "",
        sortBy: "Date",
        sortDirection: "desc",
        pageSize: 15,
        pageNumber: 1,
      });
      setSearchAlertsInputValue("");
      setSearchAlertsData([]);
      setTempSelectedUnits([]);
      setTempSelectedMetrics([]);
      setTempStartDate("");
      setTempEndDate("");
      setTempSortBy("Date");
      setTempSortDirection("desc");
    }
  };

  const applyFilters = async () => {
    try {
      setLoading(true);

      if (activeTab === 0) {
        setAssetsFilters((prev) => ({
          ...prev,
          selectedAssets: tempSelectedAssets,
          pageNumber: 1,
        }));

        setHasMoreAssets(true);
        setIsLoadingMoreAssets(false);

        // Завантажуємо дані з фільтрами ассетів
        await fetchFilteredAssets();
      } else if (activeTab === 1) {
        setAlertsFilters((prev) => ({
          ...prev,
          selectedUnits: tempSelectedUnits.map((unit) => unit.unitUId || unit),
          selectedMetrics: tempSelectedMetrics,
          startDate: tempStartDate || null,
          endDate: tempEndDate || null,
          sortBy: tempSortBy,
          sortDirection: tempSortDirection,
          searchTerm: searchAlertsInputValue,
          pageNumber: 1,
        }));

        setHasMoreAlerts(true);
        setIsLoadingMoreAlerts(false);

        await fetchFilteredAlerts();
      }

      handleFilterClose();
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };


  const fetchFilteredAssets = useCallback(async () => {
    try {
      setLoading(true);
      setIsAssetsRequestInProgress(true);

      const requestBody = {
        componentIds: [],
        filterComponentIds: tempSelectedAssets.map(asset => asset.componentId),
        sortBy: assetsFilters.sortBy,
        isAscending: assetsFilters.sortDirection === "asc",
        pageSize: 15,
        pageNumber: 1,
        searchTerm: assetsFilters.searchTerm,
      };

      const response = await MmsApiService.getComponentHealthCheckPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data) {
        const items = response.data.items || [];
        setMapData(items);
        setAssetsFilters((prev) => ({ ...prev, pageNumber: 1 }));
        setHasMoreAssets(items.length === 15);
        setTotalAssetsCount(response.data.itemsTotalCount || 0);
      } else {
        setMapData([]);
        setTotalAssetsCount(0);
      }
    } catch (error) {
      setMapData([]);
    } finally {
      setLoading(false);
      setIsAssetsRequestInProgress(false);
    }
  }, [tempSelectedAssets, assetsFilters.sortBy, assetsFilters.sortDirection, assetsFilters.searchTerm, filterTypeMetrics, companyId]);

  const fetchFilteredAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setIsAlertsRequestInProgress(true);

      const requestBody = {
        unitUIds: tempSelectedUnits.map(unit => unit.unitUId || unit),
        metrics: tempSelectedMetrics,
        startDate: tempStartDate || null,
        endDate: tempEndDate || null,
        searchTerm: searchAlertsInputValue,
        sortBy: tempSortBy,
        isAscending: tempSortDirection === "asc",
        pageSize: 15,
        pageNumber: 1,
      };

      const response = await MmsApiService.getMetricsErrorsPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data) {
        const items = response.data.items || [];
        setAlertsData(items);
        setAlertsFilters((prev) => ({ ...prev, pageNumber: 1 }));
        setHasMoreAlerts(items.length === 15);
        setTotalAlertsCount(response.data.itemsTotalCount || 0);
      } else {
        setAlertsData([]);
        setTotalAlertsCount(0);
      }
    } catch (error) {
      setAlertsData([]);
    } finally {
      setLoading(false);
      setIsAlertsRequestInProgress(false);
    }
  }, [tempSelectedUnits, tempSelectedMetrics, tempStartDate, tempEndDate, searchAlertsInputValue, tempSortBy, tempSortDirection, filterTypeMetrics, companyId]);

  const handleFilterClick = useCallback(
    (event) => {
      setFilterAnchorEl(event.currentTarget);

      if (activeTab === 0) {
        setTempSelectedAssets(assetsFilters.selectedAssets);

        if (mapData.length > 0 && allAssetsData.length === 0) {
          setAllAssetsData(mapData);
          setFilterAssetsPage(1);
          setHasMoreFilterAssets(mapData.length === 15);
        }
      } else if (activeTab === 1) {
        if (alertsData.length > 0 && allAlertsComponents.length === 0) {
          const uniqueComponents = alertsData.reduce((acc, alert) => {
            const componentName = alert.componentName;
            const componentNumber = alert.componentNumber;
            const unitUId = alert.unitUId;
            if (
              componentName &&
              unitUId &&
              !acc.find((item) => item.unitUId === unitUId)
            ) {
              acc.push({
                componentName: componentName,
                componentNumber: componentNumber,
                unitUId: unitUId,
              });
            }
            return acc;
          }, []);
          setAllAlertsComponents(uniqueComponents);
          setFilterAlertsPage(1);
          setHasMoreFilterAlerts(alertsData.length === 15);
        }

        const selectedUnitsAsObjects = alertsFilters.selectedUnits.map(
          (unitUId) => {
            const component = allAlertsComponents.find(
              (comp) => comp.unitUId === unitUId
            );
            return component
              ? {
                  componentName: component.componentName,
                  componentNumber: component.componentNumber,
                  unitUId: component.unitUId,
                }
              : {
                  componentName: unitUId,
                  componentNumber: "",
                  unitUId: unitUId,
                };
          }
        );
        setTempSelectedUnits(selectedUnitsAsObjects);
        setTempSelectedMetrics(alertsFilters.selectedMetrics);
        setTempStartDate(alertsFilters.startDate || "");
        setTempEndDate(alertsFilters.endDate || "");
        setTempSortBy(alertsFilters.sortBy);
        setTempSortDirection(alertsFilters.sortDirection);
        setSearchAlertsInputValue(alertsFilters.searchTerm);

        if (availableMetrics.length === 0 && companyId) {
          loadAvailableMetrics();
        }
      }
    },
    [
      activeTab,
      assetsFilters.selectedAssets,
      mapData,
      allAssetsData.length,
      alertsData,
      allAlertsComponents,
      alertsFilters.selectedUnits,
      alertsFilters.selectedMetrics,
      alertsFilters.startDate,
      alertsFilters.endDate,
      alertsFilters.sortBy,
      alertsFilters.sortDirection,
      alertsFilters.searchTerm,
      availableMetrics.length,
      companyId,
      loadAvailableMetrics,
    ]
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleAssetsPanel = () => {
    setShowAssetsPanel(!showAssetsPanel);
  };

  const closeAssetsPanel = () => {
    setShowAssetsPanel(false);
  };

  const handleAssetClick = useCallback(
    async (component) => {
      setSelectedAsset(component);
      setSelectedAssetInList(component);
      setModalOpen(true);
      setIsAssetClicked(true);

      if (!showAssetsPanel) {
        setShowAssetsPanel(true);
      }

      try {
        const response = await MmsApiService.getMapDataById(
          component.componentId,
          filterTypeMetrics
        );

        if (response && response.data) {
          setSelectedAsset({
            ...response.data,
            componentState:
              response.data.componentState || component.componentState,
          });
          setSelectedAssetInList({
            ...response.data,
            componentId: component.componentId,
          });
        }
      } catch (error) {
      }

      if (map.current) {
        const coordinates = getCoordinatesFromJobProperties(
          component.jobProperties
        );
        if (coordinates) {
          const [lng, lat] = coordinates;
          const boundedLng = Math.max(-180, Math.min(180, lng));
          const boundedLat = Math.max(-85, Math.min(85, lat));

          map.current.flyTo({
            center: [boundedLng, boundedLat],
            zoom: 8,
            duration: 800,
            curve: 1.2,
            speed: 1.2,
          });
        }
      }
    },
    [filterTypeMetrics, showAssetsPanel]
  );

  const handleAlertClick = (alert) => {
    if (map.current && alert.coordinates) {
      const [lng, lat] = alert.coordinates;
      const boundedLng = Math.max(-180, Math.min(180, lng));
      const boundedLat = Math.max(-85, Math.min(85, lat));

      map.current.flyTo({
        center: [boundedLng, boundedLat],
        zoom: 8,
        duration: 800,
        curve: 1.2,
        speed: 1.2,
      });
    }
  };

  const handleModalCloseStart = () => {
    setIsAssetClicked(false);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedAsset(null);
    setSelectedAssetInList(null);
    setIsAssetClicked(false);

    setTimeout(() => {
      if (map.current && map.current.resize) {
        map.current.resize();
      }
    }, 500);
  };

  const getHealthStatusColor = (healthStatus) => {
    switch (healthStatus?.toLowerCase()) {
      case "healthy":
        return "#4caf50";
      case "warning":
        return "#ff9800";
      case "critical":
        return "#f44336";
      default:
        return "#9e9e9e";
    }
  };

  const getHealthStatusStyle = (healthStatus) => {
    const color = getHealthStatusColor(healthStatus);
    return {
      backgroundColor: color,
      color: "white",
      fontWeight: 600,
      fontSize: 12,
      padding: "2px 6px",
      borderRadius: 8,
      display: "inline-block",
      textAlign: "center",
      marginLeft: 8,
    };
  };

  const getCoordinatesFromJobProperties = (jobProperties) => {
    const latitude = jobProperties?.find(
      (prop) => prop.name === "Position_Latitude"
    )?.value;
    const longitude = jobProperties?.find(
      (prop) => prop.name === "Position_Longitude"
    )?.value;

    if (latitude && longitude) {
      return [parseFloat(longitude), parseFloat(latitude)];
    }
    return null;
  };

  const createMarkers = useCallback(
    (forceRecreate = false) => {
      if (!map.current || !mapData.length) return;

      if (markersRef.current.length > 0 && !forceRecreate) {
        const validMarkers = markersRef.current.filter((marker) => {
          try {
            return (
              marker && marker.getElement && marker.getElement().parentNode
            );
          } catch (e) {
            return false;
          }
        });

        if (validMarkers.length === markersRef.current.length) {
          return;
        } else {
          markersRef.current.forEach((marker) => {
            try {
              if (marker && marker.remove) marker.remove();
            } catch (e) {
            }
          });
          markersRef.current = [];
        }
      }

      markersRef.current.forEach((marker) => {
        try {
          if (marker && marker.remove) marker.remove();
        } catch (e) {
        }
      });
      markersRef.current = [];

      mapData.forEach((component, index) => {
        const coordinates = getCoordinatesFromJobProperties(
          component.jobProperties
        );
        if (!coordinates) {
          return;
        }

        const [lng, lat] = coordinates;
        if (
          typeof lng !== "number" ||
          typeof lat !== "number" ||
          lng < -180 ||
          lng > 180 ||
          lat < -90 ||
          lat > 90
        ) {
          return;
        }

        const markerEl = document.createElement("div");
        markerEl.className = "asset-marker";
        markerEl.style.cssText = `
        position: absolute;
        width: 40px;
        height: 40px;
        cursor: pointer;
        z-index: 1000;
        pointer-events: auto;
      `;
        markerEl.innerHTML = `
        <div class="marker-icon" style="background-color: ${getHealthStatusColor(
          component.healthStatus
        )}">
          <img src="${engineIcon}" alt="Engine" style="width: 20px; height: 20px;" />
        </div>
      `;

        markerEl.addEventListener("click", () => {
          handleAssetClick(component);
        });

        const marker = new maplibregl.Marker({
          element: markerEl,
          anchor: "center",
        })
          .setLngLat([lng, lat])
          .addTo(map.current);

        markersRef.current.push(marker);
      });
    },
    [mapData, handleAssetClick]
  );

  const checkAndRestoreMarkers = useCallback(() => {
    if (!map.current || !mapData.length) return;

    const validMarkers = markersRef.current.filter((marker) => {
      try {
        return marker && marker.getElement && marker.getElement().parentNode;
      } catch (e) {
        return false;
      }
    });

    if (validMarkers.length < mapData.length) {
      createMarkers(true);
    }
  }, [mapData, createMarkers]);

  const loadAssetsData = useCallback(async () => {
    if (isAssetsRequestInProgress) {
      return;
    }

    try {
      setLoading(true);
      setIsAssetsRequestInProgress(true);
      
      const assetsRequestBody = {
        componentIds: [],
        filterComponentIds: [],
        sortBy: "LastHealthCheckDate",
        isAscending: false,
        pageSize: 15,
        pageNumber: 1,
      };

      const assetsResponse = await MmsApiService.getComponentHealthCheckPaged(
        assetsRequestBody,
        filterTypeMetrics,
        companyId
      );

      if (assetsResponse && assetsResponse.data) {
        const items = assetsResponse.data.items || [];
        setMapData(items);
        setAssetsFilters((prev) => ({ ...prev, pageNumber: 1 }));
        setHasMoreAssets(items.length === 15);
        setTotalAssetsCount(assetsResponse.data.itemsTotalCount || 0);
      } else {
        setMapData([]);
        setTotalAssetsCount(0);
      }
    } catch (error) {
      setMapData([]);
    } finally {
      setLoading(false);
      setIsAssetsRequestInProgress(false);
    }
  }, [companyId, filterTypeMetrics, isAssetsRequestInProgress]);

  const loadAlertsData = useCallback(async () => {
    if (isAlertsRequestInProgress) {
      return;
    }

    try {
      setLoading(true);
      setIsAlertsRequestInProgress(true);
      
      const alertsRequestBody = {
        unitUIds: [],
        metrics: [],
        startDate: null,
        endDate: null,
        searchTerm: "",
        sortBy: "Date",
        isAscending: false,
        pageSize: 15,
        pageNumber: 1,
      };

      const alertsResponse = await MmsApiService.getMetricsErrorsPaged(
        alertsRequestBody,
        filterTypeMetrics,
        companyId
      );

      if (alertsResponse && alertsResponse.data) {
        setAlertsData(alertsResponse.data.items || []);
        setAlertsFilters((prev) => ({ ...prev, pageNumber: 1 }));
        setHasMoreAlerts((alertsResponse.data.items || []).length === 15);
        setTotalAlertsCount(alertsResponse.data.itemsTotalCount || 0);
      } else {
        setAlertsData([]);
        setTotalAlertsCount(0);
      }
    } catch (error) {
      setAlertsData([]);
    } finally {
      setLoading(false);
      setIsAlertsRequestInProgress(false);
    }
  }, [companyId, filterTypeMetrics, isAlertsRequestInProgress]);


  const loadMoreAssets = useCallback(async () => {
    if (isLoadingMoreAssets || !hasMoreAssets) {
      return;
    }

    setIsLoadingMoreAssets(true);
    try {
      const currentPage = assetsFilters.pageNumber + 1;
      
      const savedAssetsFilters = localStorage.getItem('mapAssetsFilters');
      let requestBody;
      
      if (savedAssetsFilters) {
        try {
          const parsedFilters = JSON.parse(savedAssetsFilters);
          requestBody = {
            componentIds: [],
            filterComponentIds: parsedFilters.selectedAssets?.map(asset => asset.componentId) || [],
            sortBy: parsedFilters.sortBy || "LastHealthCheckDate",
            isAscending: parsedFilters.sortDirection === "asc",
            pageSize: 15,
            pageNumber: currentPage,
            searchTerm: parsedFilters.searchTerm || "",
          };
        } catch (e) {
          requestBody = {
            componentIds: [],
            filterComponentIds: [],
            sortBy: "LastHealthCheckDate",
            isAscending: false,
            pageSize: 15,
            pageNumber: currentPage,
          };
        }
      } else {
        requestBody = {
          componentIds: [],
          filterComponentIds: [],
          sortBy: "LastHealthCheckDate",
          isAscending: false,
          pageSize: 15,
          pageNumber: currentPage,
        };
      }

      const response = await MmsApiService.getComponentHealthCheckPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data) {
        const newItems = response.data.items || [];
        setMapData((prevData) => [...prevData, ...newItems]);
        setAssetsFilters((prev) => ({ ...prev, pageNumber: currentPage }));
        setHasMoreAssets(newItems.length === 15);
      }
    } catch (error) {
    } finally {
      setIsLoadingMoreAssets(false);
    }
  }, [isLoadingMoreAssets, hasMoreAssets, assetsFilters.pageNumber, filterTypeMetrics, companyId]);

  const loadMoreAlerts = useCallback(async () => {
    if (isLoadingMoreAlerts || !hasMoreAlerts) return;

    setIsLoadingMoreAlerts(true);
    try {
      const currentPage = alertsFilters.pageNumber + 1;
      
      const savedAlertsFilters = localStorage.getItem('mapAlertsFilters');
      let requestBody;
      
      if (savedAlertsFilters) {
        try {
          const parsedFilters = JSON.parse(savedAlertsFilters);
          requestBody = {
            unitUIds: parsedFilters.selectedUnits?.map(unit => unit.unitUId || unit) || [],
            metrics: parsedFilters.selectedMetrics || [],
            startDate: parsedFilters.startDate || null,
            endDate: parsedFilters.endDate || null,
            searchTerm: parsedFilters.searchTerm || "",
            sortBy: parsedFilters.sortBy || "Date",
            isAscending: parsedFilters.sortDirection === "asc",
            pageSize: 15,
            pageNumber: currentPage,
          };
        } catch (e) {
          requestBody = {
            unitUIds: [],
            metrics: [],
            startDate: null,
            endDate: null,
            searchTerm: "",
            sortBy: "Date",
            isAscending: false,
            pageSize: 15,
            pageNumber: currentPage,
          };
        }
      } else {
        requestBody = {
          unitUIds: [],
          metrics: [],
          startDate: null,
          endDate: null,
          searchTerm: "",
          sortBy: "Date",
          isAscending: false,
          pageSize: 15,
          pageNumber: currentPage,
        };
      }

      const response = await MmsApiService.getMetricsErrorsPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data) {
        const newItems = response.data.items || [];
        setAlertsData((prevData) => [...prevData, ...newItems]);
        setAlertsFilters((prev) => ({ ...prev, pageNumber: currentPage }));
        setHasMoreAlerts(newItems.length === 15);
      }
    } catch (error) {
    } finally {
      setIsLoadingMoreAlerts(false);
    }
  }, [isLoadingMoreAlerts, hasMoreAlerts, alertsFilters.pageNumber, filterTypeMetrics, companyId]);

  const loadAssetsWithSavedFilters = useCallback(async () => {
    if (isAssetsRequestInProgress) {
      return;
    }

    try {
      setLoading(true);
      setIsAssetsRequestInProgress(true);
      
      const savedAssetsFilters = localStorage.getItem('mapAssetsFilters');
      if (!savedAssetsFilters) {
        loadAssetsData();
        return;
      }

      const parsedFilters = JSON.parse(savedAssetsFilters);
      
      const requestBody = {
        componentIds: [],
        filterComponentIds: parsedFilters.selectedAssets?.map(asset => asset.componentId) || [],
        sortBy: parsedFilters.sortBy || "LastHealthCheckDate",
        isAscending: parsedFilters.sortDirection === "asc",
        pageSize: 15,
        pageNumber: 1,
        searchTerm: parsedFilters.searchTerm || "",
      };

      const response = await MmsApiService.getComponentHealthCheckPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data) {
        const items = response.data.items || [];
        setMapData(items);
        setAssetsFilters((prev) => ({ ...prev, pageNumber: 1 }));
        setHasMoreAssets(items.length === 15);
        setTotalAssetsCount(response.data.itemsTotalCount || 0);
      } else {
        setMapData([]);
        setTotalAssetsCount(0);
      }
    } catch (error) {
      setMapData([]);
    } finally {
      setLoading(false);
      setIsAssetsRequestInProgress(false);
    }
  }, [companyId, filterTypeMetrics, isAssetsRequestInProgress, loadAssetsData]);

  const loadAlertsWithSavedFilters = useCallback(async () => {
    if (isAlertsRequestInProgress) {
      return;
    }

    try {
      setLoading(true);
      setIsAlertsRequestInProgress(true);
      
      const savedAlertsFilters = localStorage.getItem('mapAlertsFilters');
      if (!savedAlertsFilters) {
        loadAlertsData();
        return;
      }

      const parsedFilters = JSON.parse(savedAlertsFilters);
      
      const requestBody = {
        unitUIds: parsedFilters.selectedUnits?.map(unit => unit.unitUId || unit) || [],
        metrics: parsedFilters.selectedMetrics || [],
        startDate: parsedFilters.startDate || null,
        endDate: parsedFilters.endDate || null,
        searchTerm: parsedFilters.searchTerm || "",
        sortBy: parsedFilters.sortBy || "Date",
        isAscending: parsedFilters.sortDirection === "asc",
        pageSize: 15,
        pageNumber: 1,
      };

      const response = await MmsApiService.getMetricsErrorsPaged(
        requestBody,
        filterTypeMetrics,
        companyId
      );

      if (response && response.data) {
        setAlertsData(response.data.items || []);
        setAlertsFilters((prev) => ({ ...prev, pageNumber: 1 }));
        setHasMoreAlerts((response.data.items || []).length === 15);
        setTotalAlertsCount(response.data.itemsTotalCount || 0);
      } else {
        setAlertsData([]);
        setTotalAlertsCount(0);
      }
    } catch (error) {
      setAlertsData([]);
    } finally {
      setLoading(false);
      setIsAlertsRequestInProgress(false);
    }
  }, [companyId, filterTypeMetrics, isAlertsRequestInProgress, loadAlertsData]);

  useEffect(() => {
    if (companyId && !hasInitialDataLoadedRef.current) {
      const savedAssetsFilters = localStorage.getItem('mapAssetsFilters');
      let hasAssetsFilters = false;
      
      if (savedAssetsFilters) {
        try {
          const parsedAssetsFilters = JSON.parse(savedAssetsFilters);
          hasAssetsFilters = parsedAssetsFilters.selectedAssets?.length > 0 || parsedAssetsFilters.searchTerm;
        } catch (e) {
        }
      }
      
      const savedAlertsFilters = localStorage.getItem('mapAlertsFilters');
      let hasAlertsFilters = false;
      
      if (savedAlertsFilters) {
        try {
          const parsedAlertsFilters = JSON.parse(savedAlertsFilters);
          hasAlertsFilters = parsedAlertsFilters.selectedUnits?.length > 0 || 
                            parsedAlertsFilters.selectedMetrics?.length > 0 || 
                            parsedAlertsFilters.startDate || 
                            parsedAlertsFilters.endDate || 
                            parsedAlertsFilters.searchTerm;
        } catch (e) {
        }
      }
      
      if (hasAssetsFilters) {
        loadAssetsWithSavedFilters();
      } else {
        loadAssetsData();
      }
      
      if (hasAlertsFilters) {
        loadAlertsWithSavedFilters();
      } else {
        loadAlertsData();
      }
      
      hasInitialDataLoadedRef.current = true;
    }
  }, [companyId, loadAssetsWithSavedFilters, loadAlertsWithSavedFilters, loadAssetsData, loadAlertsData]);

  useEffect(() => {
    if (map.current) {
      return;
    }

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
          },
        },
        layers: [
          {
            id: "osm-tiles",
            type: "raster",
            source: "osm",
            minzoom: 0,
            maxzoom: 22,
          },
        ],
      },
      center: [0, 20],
      zoom: 2,
      projection: "mercator",
      renderWorldCopies: false,
      maxBounds: [
        [-179.99, -84.99],
        [179.99, 84.99],
      ],
      maxPitch: 0,
      dragRotate: false,
    });

    window.mapInstance = map.current;

    map.current.addControl(new maplibregl.NavigationControl(), "top-right");
    map.current.addControl(new maplibregl.FullscreenControl(), "top-right");
    map.current.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      }),
      "top-right"
    );

    map.current.on("load", () => {
      setMapLoaded(true);
    });

    map.current.on("moveend", () => {
      setTimeout(() => {
        checkAndRestoreMarkers();
      }, 200);
    });

    map.current.on("zoomend", () => {
      setTimeout(() => {
        checkAndRestoreMarkers();
      }, 200);
    });

    return () => {
      if (markersRef.current.length > 0) {
        markersRef.current.forEach((marker) => marker.remove());
        markersRef.current = [];
      }

      if (map.current) {
        map.current.remove();
        map.current = null;
      }

      if (window.mapInstance) {
        delete window.mapInstance;
      }
    };
  }, [checkAndRestoreMarkers]);

  useEffect(() => {
    if (map.current) {
      setTimeout(() => {
        map.current.resize();
      }, 300);
    }
  }, [showAssetsPanel, modalOpen]);

  useEffect(() => {
    if (mapLoaded && mapData.length > 0) {
      createMarkers();
    }
  }, [mapLoaded, mapData, createMarkers]);

  useEffect(() => {
    if (mapLoaded && mapData.length > 0 && markersRef.current.length === 0) {
      createMarkers(true);
    }
  }, [mapLoaded, mapData, createMarkers]);

  useEffect(() => {
    if (!mapLoaded || !mapData.length) return;

    const interval = setInterval(() => {
      checkAndRestoreMarkers();
    }, 10000);

    return () => clearInterval(interval);
  }, [mapLoaded, mapData, checkAndRestoreMarkers]);

  useEffect(() => {
    if (map.current && !modalOpen) {
      const timer = setTimeout(() => {
        if (map.current && map.current.resize) {
          map.current.resize();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  useEffect(() => {
    const globalScrollHandler = (event) => {
      const target = event.target;
      
      if (target && target.style && 
          (target.style.overflow === 'auto' || target.style.overflow === 'hidden auto') && 
          target.style.maxHeight && 
          (target.style.maxHeight.includes('100vh') || target.style.maxHeight.includes('calc'))) {
        
        const threshold = 100;
        const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
        
        if (isNearBottom) {
          const isMainListScroll = !filterAnchorEl;
          
          if (isMainListScroll) {
            if (activeTab === 0) {
              loadMoreAssets();
            } else {
              loadMoreAlerts();
            }
          }
        }
      }
    };
    
    document.addEventListener('scroll', globalScrollHandler, true);
    
    return () => {
      document.removeEventListener('scroll', globalScrollHandler, true);
    };
  }, [loadMoreAssets, loadMoreAlerts, activeTab, filterAnchorEl]);

  useEffect(() => {
    const handleFilterAssetsScroll = (event) => {
      const target = event.target;
      
      if (target && target.closest && target.closest('.MuiAutocomplete-popper')) {
        const threshold = 100;
        const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
        
        if (isNearBottom && hasMoreFilterAssets && !isLoadingMoreFilterAssets && activeTab === 0) {
          loadMoreFilterAssets();
        }
      }
    };

    document.addEventListener('scroll', handleFilterAssetsScroll, true);
    
    return () => {
      document.removeEventListener('scroll', handleFilterAssetsScroll, true);
    };
  }, [hasMoreFilterAssets, isLoadingMoreFilterAssets, activeTab, loadMoreFilterAssets]);

  useEffect(() => {
    const handleFilterAlertsScroll = (event) => {
      const target = event.target;
      
      if (target && target.closest && target.closest('.MuiAutocomplete-popper')) {
        const threshold = 100;
        const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
        
        if (isNearBottom && hasMoreFilterAlerts && !isLoadingMoreFilterAlerts && activeTab === 1) {
          loadMoreFilterAlerts();
        }
      }
    };

    document.addEventListener('scroll', handleFilterAlertsScroll, true);
    
    return () => {
      document.removeEventListener('scroll', handleFilterAlertsScroll, true);
    };
  }, [hasMoreFilterAlerts, isLoadingMoreFilterAlerts, activeTab, loadMoreFilterAlerts]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <Box style={{ height: "100vh", overflow: "hidden", margin: 0, padding: 0 }}>
      <Paper
        elevation={2}
        className="map-container"
        style={{
          position: "relative",
          height: "100vh",
          width: "100vw",
          overflow: "hidden",
          margin: 0,
          padding: 0,
          borderRadius: 0,
        }}
      >
        <div
          ref={mapContainer}
          style={{
            height: "100%",
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />

        <Box
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            zIndex: 1000,
          }}
        >
          <Paper
            elevation={3}
            style={{
              padding: "8px 16px",
              cursor: "pointer",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              transition: "all 0.2s ease",
            }}
            onClick={toggleAssetsPanel}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 1)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.95)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#333",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              <img
                src={engineIcon}
                alt="Assets"
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
              />
              {translation.assetsTitle || "ASSETS"}
              <span
                style={{
                  backgroundColor: "#e0e0e0",
                  borderRadius: "50%",
                  padding: "2px 4px",
                  fontSize: "10px",
                  minWidth: "16px",
                  minHeight: "16px",
                  textAlign: "center",
                  marginLeft: "2px",
                }}
              >
                {totalAssetsCount}
              </span>
            </Typography>
          </Paper>
        </Box>

        {showAssetsPanel && (
          <Paper
            elevation={3}
            className={`assets-panel-overlay ${
              isAssetClicked ? "asset-clicked" : ""
            }`}
            style={{
              animation: "slideInFromLeft 0.3s ease-out forwards",
              transform: "translateX(-100%)",
              zIndex: modalOpen ? 1202 : 1000,
              position: "relative",
            }}
          >
            <Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                  style={{ flex: 1 }}
                >
                  <Tab
                    label={
                      <Box display="flex" alignItems="center">
                        <Typography style={{ marginRight: 4 }}>
                          {translation.assetsTitle || "Assets"}
                        </Typography>
                        <span
                          style={{
                            backgroundColor: "#e0e0e0",
                            borderRadius: "50%",
                            padding: "2px 6px",
                            fontSize: "10px",
                            minWidth: "20px",
                            textAlign: "center",
                          }}
                        >
                          {totalAssetsCount}
                        </span>
                      </Box>
                    }
                  />
                  <Tab
                    label={
                      <Box display="flex" alignItems="center">
                        <Typography style={{ marginRight: 4 }}>
                          Alerts
                        </Typography>
                        <span
                          style={{
                            backgroundColor: "#ffebee",
                            borderRadius: "50%",
                            padding: "2px 6px",
                            fontSize: "10px",
                            minWidth: "20px",
                            textAlign: "center",
                            color: "#d32f2f",
                          }}
                        >
                          {totalAlertsCount}
                        </span>
                      </Box>
                    }
                  />
                </Tabs>
                <IconButton
                  size="small"
                  onClick={handleFilterClick}
                  style={{
                    marginRight: "8px",
                    color: "#666",
                  }}
                >
                  <TuneIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={closeAssetsPanel}
                  style={{
                    marginRight: "8px",
                    color: "#666",
                  }}
                >
                  <CloseIcon size="small" />
                </IconButton>
              </Box>
            </Box>

            <Box
              ref={scrollContainerRef}
              style={{
                maxHeight: "calc(100vh - 200px)",
                overflowY: "auto",
                overflowX: "hidden",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                position: "relative",
                zIndex: 1,
              }}
            >
              {activeTab === 0 &&
                mapData.map((component) => {
                  return (
                    <Box
                      key={component.componentId}
                      className={`asset-item ${
                        selectedAssetInList &&
                        selectedAssetInList.componentId ===
                          component.componentId
                          ? "selected"
                          : ""
                      }`}
                      p={1.5}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleAssetClick(component)}
                    >
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="body2"
                          style={{
                            flex: 1,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          {component.componentName} |{" "}
                          {component.componentNumber}
                        </Typography>
                        <span
                          style={getHealthStatusStyle(component.healthStatus)}
                        >
                          {component.healthStatus}
                        </span>
                      </Box>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: "10px",
                          color: "#999",
                          marginTop: 2,
                          marginBottom: 4,
                        }}
                      >
                        {component.departmentName || ""}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: "10px",
                          color: "#999",
                          marginTop: 2,
                          marginBottom: 4,
                        }}
                      >
                        Last issue date:{" "}
                        {formatTimestamp(component.lastHealthCheckDataDate)}
                      </Typography>
                      <Box display="flex" alignItems="center" width="100%">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <SegmentedProgressBar
                            value={component.healthScore || 0}
                          />
                          <span
                            style={{
                              fontWeight: 500,
                              fontSize: 14,
                              minWidth: 38,
                              textAlign: "right",
                            }}
                          >
                            {Math.round(component.healthScore || 0)}%
                          </span>
                        </div>
                      </Box>
                    </Box>
                  );
                })}

              {activeTab === 0 && isLoadingMoreAssets && (
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "16px",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              {activeTab === 1 &&
                alertsData.map((alert, index) => {
                  return (
                    <Box
                      key={`${alert.componentId}-${index}`}
                      className="asset-item"
                      p={1.5}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleAlertClick(alert)}
                    >
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: "10px",
                          color: "#999",
                          marginBottom: 4,
                        }}
                      >
                        {alert.date}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          marginBottom: 4,
                        }}
                      >
                        {alert.componentName} | {alert.componentNumber}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          marginBottom: 4,
                        }}
                      >
                        Metric: {alert.metric}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "11px", color: "#888" }}
                      >
                        Anomalies count: {alert.anomalyCount}
                      </Typography>
                    </Box>
                  );
                })}

              {/* Loading indicator for alerts */}
              {activeTab === 1 && isLoadingMoreAlerts && (
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "16px",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          </Paper>
        )}

        <Box
          style={{
            position: "absolute",
            top: "10px",
            right: "50px",
            zIndex: 800,
          }}
        >
          <div
            className={
              filterTypeMetrics === false ? "optionsSwitch" : "optionsSwitch in"
            }
            style={{ minWidth: "210px", height: "36px" }}
          >
            <div className="option" onClick={toggleFilterTypeMetrics}>
              Imperial
            </div>
            <div className="option" onClick={toggleFilterTypeMetrics}>
              Metric
            </div>
          </div>
        </Box>

        {loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
            className="loaderHolder"
          >
            <CircularProgress />
          </div>
        )}
      </Paper>

      <Popover
        anchorEl={filterAnchorEl}
        keepMounted
        open={Boolean(filterAnchorEl)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleFilterClose}
      >
        <div className="activitiesFilterHolder respModal">
          <div className="filterHeading">
            <div className="pmPageBlockTitle">
              {activeTab === 0 ? "Filter Assets" : "Filter Alerts"}
            </div>
          </div>

          {activeTab === 0 ? (
            <div className="formHolder">
              <Autocomplete
                multiple
                options={(() => {
                  const allAssets = allAssetsData || [];
                  const searchResults = searchAssetsData || [];

                  if (searchInputValue.trim()) {
                    return searchResults;
                  }

                  return allAssets;
                })()}
                getOptionLabel={(option) =>
                  `${option.componentName} (${option.componentNumber})`
                }
                value={tempSelectedAssets}
                getOptionSelected={(option, value) =>
                  option.componentId === value.componentId
                }
                onChange={(event, newValue) => {
                  setTempSelectedAssets(newValue);

                  setSearchInputValue("");
                  setSearchAssetsData([]);
                }}
                onInputChange={(event, newInputValue) => {
                  setSearchInputValue(newInputValue);
                  debouncedSearch(newInputValue);
                }}
                inputValue={searchInputValue}
                className="autocompleteBox"
                disableCloseOnSelect
                renderTags={(value, getTagProps) => {
                  if (value.length > 0) {
                    return (
                      <span
                        style={{
                          color: "#444444e0",
                          fontSize: "13px",
                        }}
                      >
                        Selected assets: {value.length}
                      </span>
                    );
                  }
                  return null;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Assets"
                    variant="outlined"
                    fullWidth
                    className="autocomplete"
                    InputProps={{ ...params.InputProps, type: "search" }}
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      tempSelectedAssets.length > 0
                        ? ""
                        : "Choose assets to filter"
                    }
                  />
                )}
                renderOption={(option, { selected }) => (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={selected}
                      size="small"
                      style={{ padding: "4px", marginRight: "6px" }}
                    />
                    {`${option.componentName} (${option.componentNumber})`}
                  </div>
                )}
              />

              <TextField
                value={assetsFilters.sortBy}
                onChange={(e) =>
                  handleAssetsFilterChange("sortBy", e.target.value)
                }
                label="Sort by"
                variant="outlined"
                fullWidth
                select
                margin="normal"
              >
                <MenuItem value="LastHealthCheckDate">
                  Last Health Check Date
                </MenuItem>
                <MenuItem value="ComponentName">Component Name</MenuItem>
                <MenuItem value="HealthScore">Health Score</MenuItem>
              </TextField>

              <TextField
                value={assetsFilters.sortDirection}
                onChange={(e) =>
                  handleAssetsFilterChange("sortDirection", e.target.value)
                }
                label="Sort Direction"
                variant="outlined"
                fullWidth
                select
                margin="normal"
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </TextField>
            </div>
          ) : (
            <div className="formHolder">
              <Autocomplete
                multiple
                options={(() => {
                  const allComponents = allAlertsComponents || [];
                  const searchResults = searchAlertsData || [];

                  if (searchAlertsInputValue.trim()) {
                    return searchResults;
                  }

                  return allComponents;
                })()}
                getOptionLabel={(option) =>
                  option.componentName && option.componentNumber
                    ? `${option.componentName} | ${option.componentNumber}`
                    : option.componentName || option
                }
                value={tempSelectedUnits}
                getOptionSelected={(option, value) =>
                  option.unitUId === value.unitUId
                }
                onChange={(event, newValue) => {
                  setTempSelectedUnits(newValue);

                  setSearchAlertsInputValue("");
                  setSearchAlertsData([]);
                }}
                onInputChange={(event, newInputValue) => {
                  setSearchAlertsInputValue(newInputValue);
                  debouncedAlertsSearch(newInputValue);
                }}
                inputValue={searchAlertsInputValue}
                className="autocompleteBox"
                disableCloseOnSelect
                renderTags={(value, getTagProps) => {
                  if (value.length > 0) {
                    return (
                      <span
                        style={{
                          color: "#444444e0",
                          fontSize: "13px",
                        }}
                      >
                        Selected components: {value.length}
                      </span>
                    );
                  }
                  return null;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Components"
                    variant="outlined"
                    fullWidth
                    className="autocomplete"
                    InputProps={{ ...params.InputProps, type: "search" }}
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      tempSelectedUnits.length > 0
                        ? ""
                        : "Choose components to filter"
                    }
                  />
                )}
                renderOption={(option, { selected }) => (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={selected}
                      size="small"
                      style={{ padding: "4px", marginRight: "6px" }}
                    />
                    {option.componentName && option.componentNumber
                      ? `${option.componentName} | ${option.componentNumber}`
                      : option.componentName || option}
                  </div>
                )}
              />

              <Autocomplete
                multiple
                options={availableMetrics}
                getOptionLabel={(option) => option}
                value={tempSelectedMetrics}
                onChange={(event, newValue) => setTempSelectedMetrics(newValue)}
                className="autocompleteBox"
                disableCloseOnSelect
                renderTags={(value, getTagProps) => {
                  if (value.length > 0) {
                    return (
                      <span
                        style={{
                          color: "#444444e0",
                          fontSize: "13px",
                        }}
                      >
                        Selected metrics: {value.length}
                      </span>
                    );
                  }
                  return null;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Metrics"
                    variant="outlined"
                    fullWidth
                    className="autocomplete"
                    InputProps={{ ...params.InputProps, type: "search" }}
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      tempSelectedMetrics.length > 0
                        ? ""
                        : "Choose metrics to filter"
                    }
                  />
                )}
                renderOption={(option, { selected }) => (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={selected}
                      size="small"
                      style={{ padding: "4px", marginRight: "6px" }}
                    />
                    {option}
                  </div>
                )}
              />

              <TextField
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                label="Start Date"
                variant="outlined"
                fullWidth
                type="datetime-local"
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                label="End Date"
                variant="outlined"
                fullWidth
                type="datetime-local"
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />

              {/* Sort by selection for alerts */}
              <TextField
                value={tempSortBy}
                onChange={(e) => setTempSortBy(e.target.value)}
                label="Sort by"
                variant="outlined"
                fullWidth
                select
                margin="normal"
              >
                <MenuItem value="Date">Date</MenuItem>
                <MenuItem value="UnitUId">Unit UID</MenuItem>
                <MenuItem value="Metric">Metric</MenuItem>
                <MenuItem value="AnomalyCount">Anomaly Count</MenuItem>
              </TextField>

              {/* Sort direction for alerts */}
              <TextField
                value={tempSortDirection}
                onChange={(e) => setTempSortDirection(e.target.value)}
                label="Sort Direction"
                variant="outlined"
                fullWidth
                select
                margin="normal"
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </TextField>
            </div>
          )}

          <div
            className="btnHolder end fixedWidthBtn"
            style={{ marginTop: "15px" }}
          >
            <Button
              variant="outlined"
              className="respModal"
              onClick={resetFilters}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              className="respModal"
              onClick={applyFilters}
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>

      <AssetDetailModal
        open={modalOpen}
        onClose={handleModalClose}
        onCloseStart={handleModalCloseStart}
        asset={selectedAsset}
        filterTypeMetrics={filterTypeMetrics}
      />

      <style>{`
        @keyframes slideInFromLeft {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        html, body {
          overflow: hidden !important;
          margin: 0;
          padding: 0;
          height: 100%;
        }
        
        @keyframes segmentAppear {
          from {
            opacity: 0;
            transform: scaleY(0.3);
          }
          to {
            opacity: 1;
            transform: scaleY(1);
          }
        }
        
        .asset-marker {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
          margin: 0;
          padding: 0;
          background: none;
          box-sizing: border-box;
          cursor: pointer;
        }
        
        .asset-marker:hover {
          transform: scale(1.1);
        }
        
        .marker-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: all 0.2s ease;
        }
        
        .marker-icon:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        }
        
        
        
        .MuiTabs-root {
          min-height: 48px;
        }
        
        .MuiTab-root {
          min-height: 48px;
          font-size: 12px;
          font-weight: 600;
        }
        
        .MuiTabs-indicator {
          height: 3px;
        }
        
        .asset-item {
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.2s ease;
        }
        
        .asset-item:hover {
          background-color: rgba(0,0,0,0.05);
        }
        
        .asset-item:last-child {
          border-bottom: none;
        }
        
        .map-loading-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 0.9);
          padding: 20px;
          border-radius: 8px;
          z-index: 1000;
        }
        
        /* Ensure modal doesn't affect map rendering */
        .MuiDialog-root {
          isolation: isolate;
        }
        
        .MuiDialog-paper {
          isolation: isolate;
          contain: layout style paint;
        }
        
        #root {
          overflow: hidden !important;
          height: 100vh;
          margin: 0;
          padding: 0;
        }
        
        *, *::before, *::after {
          box-sizing: border-box;
        }
        
        .assets-panel-overlay {
          overflow-x: hidden;
          position: fixed !important;
          top: 78px;
          left: 0;
          height: calc(100vh - 88px);
          // width: 450px;
          z-index: 1202 !important;
        }
        
        .assets-panel-overlay.asset-clicked {
          z-index: 1201 !important;
        }
        
        @media (max-width: 992px) {
          .assets-panel-overlay {
            max-width: 450px !important;
          }
          
          .assets-panel-overlay.asset-clicked {
            max-width: 450px !important;
          }
        }
        
        .asset-item.selected {
          background-color: rgba(33, 150, 243, 0.1);
          border-left: 4px solid #006ae7;
        }
        
        .asset-item.selected:hover {
          background-color: rgba(33, 150, 243, 0.15);
        }
        
        /* Autocomplete specific styles to match select dropdown */
        .MuiAutocomplete-listbox {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option {
          padding: 4px 8px !important;
          min-height: auto !important;
          font-size: 13px !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option:hover {
          background-color: rgba(0, 0, 0, 0.04) !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"] {
          background-color: rgba(0, 0, 0, 0.08) !important;
        }
        
        /* Checkbox styling in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root {
          padding: 4px !important;
          margin-right: 6px !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root .MuiSvgIcon-root {
          font-size: 18px !important;
        }
        
        /* Ensure consistent spacing */
        .MuiAutocomplete-listbox .MuiAutocomplete-option > div {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
        }
        
        /* Checkbox colors in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked svg {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked path {
          fill: #006ae7 !important;
        }
        
        /* Keep unchecked checkboxes in their original color */
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) .MuiSvgIcon-root {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) svg {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) path {
          fill: inherit !important;
        }
      `}</style>
    </Box>
  );
};

export default Map;
